import React, {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {scheduleDemo} from "../requests/AppRequestPublic";

/**
 * VTMarketing2
 *
 * Marketing / sales page for VirtualTipz aimed at strip club Owners and General Managers.
 * Self-contained: hero, value props, interactive demo of the tipping flow, how-it-works,
 * revenue story, and a closing call to action.
 *
 * Styling matches the app brand: black + pink-400/500/600 accents on white/near-black,
 * Tailwind utility classes (no new config needed).
 */

/* ------------------------------------------------------------------ */
/* Interactive phone demo of the customer tipping experience           */
/* ------------------------------------------------------------------ */

function PhoneDemo() {
    const STARTING_BALANCE = 184;
    const TABLE_REQUEST_THRESHOLD = 5;

    const [balance, setBalance] = useState(STARTING_BALANCE);
    const [tippedTotal, setTippedTotal] = useState(0);
    const [pulse, setPulse] = useState(false);
    const [floaters, setFloaters] = useState([]);
    const floaterId = useRef(0);

    // Table request flow
    const [showTableForm, setShowTableForm] = useState(false);
    const [tableNumber, setTableNumber] = useState("");
    const [tableRequested, setTableRequested] = useState(false);

    const canRequestTable = tippedTotal >= TABLE_REQUEST_THRESHOLD;

    const addTip = (amount) => {
        if (balance < amount) {
            return;
        }
        setBalance((b) => b - amount);
        setTippedTotal((t) => t + amount);
        setPulse(true);
        setTimeout(() => setPulse(false), 300);

        const id = floaterId.current++;
        setFloaters((f) => [...f, {id, amount}]);
        setTimeout(() => {
            setFloaters((f) => f.filter((x) => x.id !== id));
        }, 1200);
    };

    const submitTableRequest = () => {
        if (!tableNumber || tableNumber.trim() === "") {
            return;
        }
        setTableRequested(true);
        setShowTableForm(false);
    };

    const reset = () => {
        setBalance(STARTING_BALANCE);
        setTippedTotal(0);
        setFloaters([]);
        setShowTableForm(false);
        setTableNumber("");
        setTableRequested(false);
    };

    return (
        <div className="relative mx-auto w-full max-w-[320px]">
            {/* Phone frame */}
            <div className="relative rounded-[2.5rem] border-[10px] border-gray-900 bg-gray-900 shadow-2xl">
                {/* Notch */}
                <div className="absolute left-1/2 top-0 z-20 h-6 w-32 -translate-x-1/2 rounded-b-2xl bg-gray-900"/>
                {/* Screen */}
                <div className="relative overflow-hidden rounded-[1.9rem] bg-gray-50">
                    <div className="flex flex-col items-center px-6 pb-10 pt-12 text-center">
                        <p className="text-2xl font-semibold text-gray-900">Now on stage</p>
                        <h3 className="mt-4 text-5xl font-bold tracking-tight text-gray-900">Bunny</h3>

                        {/* Floating tip animations */}
                        <div className="pointer-events-none absolute inset-x-0 top-40 flex justify-center">
                            {floaters.map((f) => (
                                <span
                                    key={f.id}
                                    className="absolute animate-[floatUp_1.2s_ease-out_forwards] text-2xl font-bold text-pink-500"
                                >
                                    +${f.amount}
                                </span>
                            ))}
                        </div>

                        {/* Tip button */}
                        <button
                            type="button"
                            onClick={() => addTip(1)}
                            className={
                                "mt-8 flex w-44 items-center justify-center gap-3 rounded-2xl bg-pink-600 py-5 text-2xl font-bold text-white shadow-lg transition-transform hover:bg-pink-500 active:scale-95 " +
                                (pulse ? "scale-105" : "scale-100")
                            }
                        >
                            TIP
                            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-pink-600">
                                $
                            </span>
                        </button>

                        <p className="mt-6 text-base font-semibold text-gray-900">
                            You have tipped Bunny a total of {tippedTotal} VT
                        </p>

                        <div className="mt-4 rounded-xl bg-pink-50 px-5 py-3 ring-1 ring-pink-100">
                            <span className="text-lg font-bold text-gray-900">{balance} </span>
                            <span className="text-lg font-medium text-gray-700">VirtualTipz remaining</span>
                        </div>

                        {/* Table request — unlocks after reaching the tip threshold */}
                        <div className="mt-6 w-full">
                            {tableRequested ? (
                                <div className="rounded-xl bg-green-50 px-4 py-3 ring-1 ring-green-200">
                                    <p className="text-sm font-semibold text-green-700">
                                        ✓ Request sent to Bunny
                                    </p>
                                    <p className="mt-0.5 text-xs text-green-600">
                                        She&apos;ll be told you&apos;re at table {tableNumber}
                                    </p>
                                </div>
                            ) : showTableForm ? (
                                <div className="rounded-xl bg-white px-4 py-4 ring-1 ring-gray-200">
                                    <label className="block text-left text-xs font-semibold text-gray-700">
                                        Your table number
                                    </label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={tableNumber}
                                        onChange={(e) => setTableNumber(e.target.value)}
                                        placeholder="e.g. 12"
                                        className="mt-1.5 block w-full rounded-lg border-0 py-2 text-center text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-pink-600 sm:text-sm"
                                    />
                                    <button
                                        type="button"
                                        onClick={submitTableRequest}
                                        disabled={!tableNumber || tableNumber.trim() === ""}
                                        className="mt-3 w-full rounded-lg bg-pink-600 py-2.5 text-sm font-semibold text-white transition hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        Submit request
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setShowTableForm(true)}
                                    disabled={!canRequestTable}
                                    className={
                                        "w-full rounded-xl py-3 text-sm font-semibold transition " +
                                        (canRequestTable
                                            ? "bg-gray-900 text-white hover:bg-gray-800"
                                            : "cursor-not-allowed bg-gray-100 text-gray-400 ring-1 ring-gray-200")
                                    }
                                >
                                    {canRequestTable
                                        ? "🪑 Request to my table"
                                        : `Tip ${TABLE_REQUEST_THRESHOLD - tippedTotal} more VT to unlock table request`}
                                </button>
                            )}
                        </div>

                        {balance <= 0 && (
                            <button
                                type="button"
                                onClick={reset}
                                className="mt-5 text-sm font-medium text-pink-600 underline hover:text-pink-500"
                            >
                                Reload demo balance
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <p className="mt-4 text-center text-sm text-gray-500">
                Live demo &mdash; tap <span className="font-semibold text-pink-600">TIP</span> to try it
            </p>

            {/* Keyframes for the floating tip animation */}
            <style>{`
                @keyframes floatUp {
                    0%   { opacity: 0; transform: translateY(0) scale(0.8); }
                    20%  { opacity: 1; transform: translateY(-10px) scale(1.1); }
                    100% { opacity: 0; transform: translateY(-90px) scale(1); }
                }
            `}</style>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* DJ-controlled real-time stage sync demo                             */
/* ------------------------------------------------------------------ */

function StageSyncDemo() {
    const DANCERS = ["Bunny", "Diamond", "Jade", "Scarlet"];

    const [onStageIndex, setOnStageIndex] = useState(0);
    const [justSynced, setJustSynced] = useState(false);
    const [userPicked, setUserPicked] = useState(false);

    // Flash a "synced" pulse on the phone whenever the on-stage dancer changes.
    useEffect(() => {
        setJustSynced(true);
        const t = setTimeout(() => setJustSynced(false), 650);
        return () => clearTimeout(t);
    }, [onStageIndex]);

    // Auto-rotate the stage to feel "live" until the visitor takes control.
    useEffect(() => {
        if (userPicked) {
            return;
        }
        const interval = setInterval(() => {
            setOnStageIndex((i) => (i + 1) % DANCERS.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [userPicked, DANCERS.length]);

    const selectDancer = (index) => {
        setUserPicked(true);
        setOnStageIndex(index);
    };

    const onStage = DANCERS[onStageIndex];

    return (
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            {/* DJ control panel */}
            <div className="rounded-3xl bg-gray-900 p-6 shadow-2xl ring-1 ring-white/10">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-pink-600 text-base">
                            🎧
                        </span>
                        <span className="text-sm font-semibold uppercase tracking-wide text-gray-300">
                            DJ Control
                        </span>
                    </div>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-500/15 px-3 py-1 text-xs font-semibold text-green-400 ring-1 ring-inset ring-green-500/30">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-400"/>
                        Live
                    </span>
                </div>

                <p className="mt-5 text-xs font-medium uppercase tracking-wide text-gray-500">
                    Tap a dancer to put her on stage
                </p>

                <div className="mt-3 space-y-2">
                    {DANCERS.map((name, index) => {
                        const active = index === onStageIndex;
                        return (
                            <button
                                key={name}
                                type="button"
                                onClick={() => selectDancer(index)}
                                className={
                                    "flex w-full items-center justify-between rounded-xl px-4 py-3 text-left transition " +
                                    (active
                                        ? "bg-pink-600 text-white shadow-lg"
                                        : "bg-white/5 text-gray-200 hover:bg-white/10")
                                }
                            >
                                <span className="flex items-center gap-3">
                                    <span
                                        className={
                                            "flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold " +
                                            (active ? "bg-white text-pink-600" : "bg-white/10 text-gray-300")
                                        }
                                    >
                                        {name.charAt(0)}
                                    </span>
                                    <span className="font-semibold">{name}</span>
                                </span>
                                {active && (
                                    <span className="text-xs font-semibold uppercase tracking-wide">
                                        On stage
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Customer phone that reflects the DJ's choice in real time */}
            <div className="relative mx-auto w-full max-w-[300px]">
                <div className="relative rounded-[2.5rem] border-[10px] border-gray-900 bg-gray-900 shadow-2xl">
                    <div className="absolute left-1/2 top-0 z-20 h-6 w-32 -translate-x-1/2 rounded-b-2xl bg-gray-900"/>
                    <div className="relative overflow-hidden rounded-[1.9rem] bg-gray-50">
                        <div className="flex flex-col items-center px-6 pb-10 pt-12 text-center">
                            <div
                                className={
                                    "mb-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-all duration-300 " +
                                    (justSynced
                                        ? "bg-green-100 text-green-700 opacity-100"
                                        : "bg-gray-100 text-gray-400 opacity-70")
                                }
                            >
                                <span className="h-1.5 w-1.5 rounded-full bg-green-500"/>
                                {justSynced ? "Updating live…" : "Customer view"}
                            </div>

                            <p className="text-xl font-semibold text-gray-900">Now on stage</p>
                            <h3
                                key={onStage}
                                className="mt-3 animate-[fadeSwap_0.5s_ease-out] text-4xl font-bold tracking-tight text-gray-900"
                            >
                                {onStage}
                            </h3>

                            <div className="mt-6 flex w-40 items-center justify-center gap-3 rounded-2xl bg-pink-600 py-4 text-xl font-bold text-white shadow-lg">
                                TIP
                                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-pink-600">
                                    $
                                </span>
                            </div>

                            <p className="mt-5 text-sm text-gray-500">
                                Every guest&apos;s screen shows <span className="font-semibold text-gray-900">{onStage}</span> &mdash; instantly.
                            </p>
                        </div>
                    </div>
                </div>
                <p className="mt-4 text-center text-sm text-gray-500">
                    Powered by real-time websockets
                </p>
            </div>

            <style>{`
                @keyframes fadeSwap {
                    0%   { opacity: 0; transform: translateY(8px); }
                    100% { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Admin dashboard mockup                                              */
/* ------------------------------------------------------------------ */

function DashboardMockup() {
    // Illustrative daily VirtualTipz sales for the week (in dollars). Totals $50,000.
    const week = [
        {day: "Mon", value: 4200},
        {day: "Tue", value: 5600},
        {day: "Wed", value: 4900},
        {day: "Thu", value: 6300},
        {day: "Fri", value: 9800},
        {day: "Sat", value: 12000},
        {day: "Sun", value: 7200},
    ];
    const maxValue = Math.max(...week.map((d) => d.value));

    const activity = [
        {name: "Diamond", type: "Stage tip", amount: "$20"},
        {name: "Jade", type: "Private dance", amount: "$120"},
        {name: "Bunny", type: "Stage tip", amount: "$45"},
        {name: "Scarlet", type: "Drink", amount: "$18"},
    ];

    return (
        <div className="overflow-hidden rounded-3xl bg-white dark:bg-gray-800 shadow-2xl ring-1 ring-gray-200 dark:ring-gray-700">
            {/* Window chrome */}
            <div className="flex items-center gap-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-5 py-3">
                <span className="h-3 w-3 rounded-full bg-red-400"/>
                <span className="h-3 w-3 rounded-full bg-yellow-400"/>
                <span className="h-3 w-3 rounded-full bg-green-400"/>
                <span className="ml-4 text-sm font-semibold text-gray-500 dark:text-gray-400">
                    VirtualTipz &mdash; Venue Dashboard
                </span>
            </div>

            <div className="p-6 sm:p-8">
                {/* KPI cards */}
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                    <div className="rounded-2xl bg-gray-50 dark:bg-gray-900 p-5 ring-1 ring-gray-100 dark:ring-gray-700">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">VirtualTipz Sold</p>
                        <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-gray-100">$50,000</p>
                        <p className="mt-1 text-xs font-medium text-gray-400 dark:text-gray-500">47,500 VT to guests</p>
                    </div>
                    <div className="rounded-2xl bg-gray-50 dark:bg-gray-900 p-5 ring-1 ring-gray-100 dark:ring-gray-700">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">5% Split Taken Out</p>
                        <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-gray-100">$2,500</p>
                    </div>
                    <div className="rounded-2xl bg-pink-600 p-5">
                        <p className="text-xs font-medium uppercase tracking-wide text-pink-100">Your Venue (4%)</p>
                        <p className="mt-2 text-2xl font-bold text-white">$2,000</p>
                        <p className="mt-1 text-xs font-medium text-pink-100">Upfront Profit</p>
                    </div>
                    <div className="rounded-2xl bg-gray-50 dark:bg-gray-900 p-5 ring-1 ring-gray-100 dark:ring-gray-700">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">VirtualTipz App (1%)</p>
                        <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-gray-100">$500</p>
                        <p className="mt-1 text-xs font-medium text-gray-400 dark:text-gray-500">Platform share</p>
                    </div>
                </div>

                {/* Chart + activity */}
                <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-5">
                    {/* Weekly sales chart */}
                    <div className="rounded-2xl bg-gray-50 dark:bg-gray-900 p-5 ring-1 ring-gray-100 dark:ring-gray-700 lg:col-span-3">
                        <div className="flex items-baseline justify-between">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Cashflow from VirtualTipz sells</p>
                            <p className="text-sm font-bold text-gray-900 dark:text-gray-100">
                                ${week.reduce((sum, d) => sum + d.value, 0).toLocaleString()}
                                <span className="ml-1 text-xs font-medium text-gray-500 dark:text-gray-400">this period</span>
                            </p>
                        </div>
                        <div className="mt-5 flex h-44 items-end justify-between gap-2">
                            {week.map((d) => (
                                <div key={d.day} className="flex flex-1 flex-col items-center justify-end gap-2" style={{height: "100%"}}>
                                    <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-400">${d.value.toLocaleString()}</span>
                                    <div
                                        className="w-full max-w-[26px] rounded-t-md bg-gradient-to-t from-pink-600 to-pink-400"
                                        style={{height: `${(d.value / maxValue) * 100}%`}}
                                    />
                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{d.day}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Recent activity */}
                    <div className="rounded-2xl bg-gray-50 dark:bg-gray-900 p-5 ring-1 ring-gray-100 dark:ring-gray-700 lg:col-span-2">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Sell activity</p>
                        <ul className="mt-4 space-y-3">
                            {activity.map((a, i) => (
                                <li key={i} className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-pink-100 text-xs font-bold text-pink-600">
                                            {a.name.charAt(0)}
                                        </span>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{a.name}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">{a.type}</p>
                                        </div>
                                    </div>
                                    <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{a.amount}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Small presentational helpers                                        */
/* ------------------------------------------------------------------ */

function Stat({value, label}) {
    return (
        <div className="text-center">
            <p className="text-4xl font-bold tracking-tight text-white sm:text-5xl">{value}</p>
            <p className="mt-2 text-sm font-medium uppercase tracking-wide text-pink-200">{label}</p>
        </div>
    );
}

function FeatureCard({title, body, icon}) {
    return (
        <div className="rounded-2xl bg-white dark:bg-gray-800 p-7 shadow-sm ring-1 ring-gray-100 dark:ring-gray-700 transition hover:shadow-md">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-pink-100 text-2xl">
                {icon}
            </div>
            <h3 className="mt-5 text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <p className="mt-2 text-base leading-relaxed text-gray-600 dark:text-gray-400">{body}</p>
        </div>
    );
}

function Step({number, title, body}) {
    return (
        <div className="relative pl-16">
            <div className="absolute left-0 top-0 flex h-11 w-11 items-center justify-center rounded-full bg-pink-600 text-lg font-bold text-white">
                {number}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <p className="mt-2 text-base leading-relaxed text-gray-600 dark:text-gray-400">{body}</p>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function VTMarketing2() {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);

    // Schedule a Demo / Contact form
    const EMPTY_DEMO = {name: "", email: "", phone: "", city: "", affiliatedClubs: "", message: ""};
    const [demo, setDemo] = useState(EMPTY_DEMO);
    const [demoSubmitting, setDemoSubmitting] = useState(false);
    const [demoSubmitted, setDemoSubmitted] = useState(false);
    const [demoError, setDemoError] = useState(null);

    const updateDemo = (field, value) => setDemo((d) => ({...d, [field]: value}));

    const demoSuccess = () => {
        setDemoSubmitting(false);
        setDemoSubmitted(true);
        setDemo(EMPTY_DEMO);
    };

    const demoFail = () => {
        setDemoSubmitting(false);
        setDemoError("Something went wrong sending your request. Please try again or email us directly.");
    };

    const submitDemo = () => {
        setDemoError(null);
        if (!demo.email || demo.email.trim() === "" || !demo.phone || demo.phone.trim() === "") {
            setDemoError("Please enter your email and phone number.");
            return;
        }
        setDemoSubmitting(true);
        scheduleDemo(demo, demoSuccess, demoFail);
    };

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900 font-sans text-gray-900 dark:text-gray-100">
            {/* Sticky top bar */}
            <header
                className={
                    "fixed inset-x-0 top-0 z-50 transition-all " +
                    (scrolled ? "bg-white/90 shadow-sm backdrop-blur" : "bg-transparent")
                }
            >
                <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
                    <div className="text-2xl font-extrabold tracking-tight">
                        <span className={scrolled ? "text-gray-900" : "text-white"}>VIRTUAL</span>
                        <span className="text-pink-500">TIPZ</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate("/login/mobile")}
                        className={
                            "rounded-full px-5 py-2 text-sm font-semibold transition " +
                            (scrolled
                                ? "bg-pink-600 text-white hover:bg-pink-500"
                                : "bg-white/10 text-white ring-1 ring-white/40 hover:bg-white/20")
                        }
                    >
                        Venue Login
                    </button>
                </div>
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden bg-gray-900">
                {/* Decorative gradient blobs */}
                <div className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full bg-pink-600/30 blur-3xl"/>
                <div className="pointer-events-none absolute -bottom-32 right-0 h-96 w-96 rounded-full bg-pink-500/20 blur-3xl"/>

                <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 pb-20 pt-36 lg:grid-cols-2 lg:px-8 lg:pt-40">
                    <div>
                        <span className="inline-flex items-center rounded-full bg-pink-600/20 px-4 py-1.5 text-sm font-semibold text-pink-300 ring-1 ring-inset ring-pink-500/40">
                            A new revenue stream that costs your club nothing
                        </span>
                        <h1 className="mt-6 text-5xl font-bold leading-tight tracking-tight text-white sm:text-6xl">
                            Your guests tip. <span className="text-pink-500">Your venue earns.</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-lg leading-relaxed text-gray-300">
                            VirtualTipz doesn&apos;t replace the dollar &mdash; it&apos;s another way to tip. Customers
                            buy VirtualTipz at the front desk through your existing POS, then tip dancers and request a
                            dancer to their table without ever leaving their seat. The customer covers every cost, and
                            your venue earns on every package sold.
                        </p>
                        <div className="mt-9 flex flex-wrap items-center gap-4">
                            <a
                                href="#contact"
                                className="rounded-full bg-pink-600 px-7 py-3.5 text-base font-semibold text-white shadow-lg transition hover:bg-pink-500"
                            >
                                Get VirtualTipz in your club
                            </a>
                            <a
                                href="#demo"
                                className="rounded-full px-7 py-3.5 text-base font-semibold text-white ring-1 ring-inset ring-white/30 transition hover:bg-white/10"
                            >
                                See the live demo
                            </a>
                        </div>
                    </div>

                    {/* Hero demo */}
                    <div className="flex justify-center lg:justify-end">
                        <PhoneDemo/>
                    </div>
                </div>

                {/* Stat band */}
                <div className="relative border-t border-white/10 bg-black/30">
                    <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-6 py-12 sm:grid-cols-4 lg:px-8">
                        <Stat value="$0" label="Cost to your club"/>
                        <Stat value="Live" label="Real-time stage sync"/>
                        <Stat value="100%" label="Spent at your venue"/>
                        <Stat value="New" label="Repeat-customer engine"/>
                    </div>
                </div>
            </section>

            {/* Reassurance: adds to, doesn't replace, cash tipping */}
            <section className="border-b border-gray-100 bg-pink-50">
                <div className="mx-auto flex max-w-5xl items-center gap-5 px-6 py-8 lg:px-8">
                    <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-pink-600 text-2xl text-white">
                        🤝
                    </span>
                    <p className="text-base leading-relaxed text-gray-700 sm:text-lg">
                        <span className="font-semibold text-gray-900">It never replaces the dollar.</span>{" "}
                        VirtualTipz is simply another way for customers to tip &mdash; and to request a dancer to their
                        table &mdash; without ever leaving their seat. Cash keeps working exactly as it does today.
                    </p>
                </div>
            </section>

            {/* What it is */}
            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
                <div className="mx-auto max-w-3xl text-center">
                    <h2 className="text-base font-semibold uppercase tracking-wide text-pink-600">
                        What is VirtualTipz
                    </h2>
                    <p className="mt-3 text-4xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                        The digital tipping platform built for the floor
                    </p>
                    <p className="mt-5 text-lg leading-relaxed text-gray-600 dark:text-gray-400">
                        Guests buy VirtualTipz credits (VT) right at your front desk using the POS system you already
                        run. They spend those credits across your venue &mdash; tipping dancers, paying for dances,
                        buying drinks, and sending table requests &mdash; all through one system you control.
                    </p>
                </div>

                <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
                    <FeatureCard
                        icon="💸"
                        title="One-tap stage tips"
                        body="Customers tap TIP on their phone and credits land on the right dancer instantly, with the amount shown live on screen."
                    />
                    <FeatureCard
                        icon="🪑"
                        title="Table requests"
                        body="Once a guest tips a dancer past a set amount, they can send a request for her to come to their table — turning tips into face time and bigger spends."
                    />
                    <FeatureCard
                        icon="📊"
                        title="Owner-level visibility"
                        body="See sales, tips, dances, and payouts in real time. Reconcile a shift in minutes with every transaction tracked end to end."
                    />
                </div>
            </section>

            {/* Interactive demo callout */}
            <section id="demo" className="bg-gray-50 dark:bg-gray-900 py-20">
                <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2 lg:px-8">
                    <div className="order-2 flex justify-center lg:order-1">
                        <PhoneDemo/>
                    </div>
                    <div className="order-1 lg:order-2">
                        <h2 className="text-base font-semibold uppercase tracking-wide text-pink-600">
                            Try it yourself
                        </h2>
                        <p className="mt-3 text-4xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                            This is exactly what your guests see
                        </p>
                        <p className="mt-5 text-lg leading-relaxed text-gray-600 dark:text-gray-400">
                            The DJ sets who&apos;s on stage, and every guest&apos;s phone updates in real time to show
                            the current performer. Guests tip with a single tap &mdash; the dancer sees it land, and
                            you see every dollar move through your venue.
                        </p>
                        <ul className="mt-8 space-y-4">
                            {[
                                "DJ controls the stage — guest views sync instantly",
                                "One tap to tip — no app download required",
                                "Tip past a set amount to unlock a table request",
                                "Every transaction is logged for payout and reporting",
                            ].map((item) => (
                                <li key={item} className="flex items-start gap-3">
                                    <span className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-pink-100 text-sm font-bold text-pink-600">
                                        ✓
                                    </span>
                                    <span className="text-base text-gray-700 dark:text-gray-200">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* DJ-controlled real-time stage sync */}
            <section className="bg-gray-900 py-20">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-3xl text-center">
                        <h2 className="text-base font-semibold uppercase tracking-wide text-pink-400">
                            The DJ runs the room
                        </h2>
                        <p className="mt-3 text-4xl font-bold tracking-tight text-white">
                            Change the stage, change every screen &mdash; instantly
                        </p>
                        <p className="mt-5 text-lg leading-relaxed text-gray-300">
                            There are no codes to scan. The DJ sets who&apos;s on stage from the booth, and advanced
                            real-time websocket technology pushes that change to every customer&apos;s phone at once.
                            Guests always see the dancer who&apos;s performing right now &mdash; and tip the right one.
                        </p>
                    </div>

                    <div className="mt-14">
                        <StageSyncDemo/>
                    </div>

                    <p className="mt-10 text-center text-sm text-gray-400">
                        Try it &mdash; pick a dancer in the DJ panel and watch the customer&apos;s phone update live.
                    </p>
                </div>
            </section>

            {/* How it works */}
            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
                <div className="mx-auto max-w-3xl text-center">
                    <h2 className="text-base font-semibold uppercase tracking-wide text-pink-600">
                        How it works
                    </h2>
                    <p className="mt-3 text-4xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                        Up and running on your floor in a night
                    </p>
                </div>
                <div className="mx-auto mt-14 grid max-w-5xl grid-cols-1 gap-10 sm:grid-cols-2">
                    <Step
                        number="1"
                        title="Guests buy VT at the front desk"
                        body="Customers purchase a VirtualTipz package through your existing POS — the same checkout you already use. Credits land in their phone in seconds."
                    />
                    <Step
                        number="2"
                        title="They tip, dance, drink & request tables"
                        body="Guests spend VT across the venue. Dancers get tipped on stage, book dances, and sell drinks — and once a guest tips enough, they can request a dancer to their table."
                    />
                    <Step
                        number="3"
                        title="You track everything live"
                        body="Managers see tips, sales, and who's on stage in real time from the dashboard. No guesswork, no end-of-night surprises."
                    />
                    <Step
                        number="4"
                        title="Payouts run clean"
                        body="Dancer payouts are calculated automatically with pins and history, so settling up at the end of a shift is fast and disputes drop."
                    />
                </div>
            </section>

            {/* Admin dashboard */}
            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
                <div className="mx-auto max-w-3xl text-center">
                    <h2 className="text-base font-semibold uppercase tracking-wide text-pink-600">
                        Your command center
                    </h2>
                    <p className="mt-3 text-4xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                        See the whole floor from one dashboard
                    </p>
                    <p className="mt-5 text-lg leading-relaxed text-gray-600 dark:text-gray-400">
                        Managers and owners get a real-time view of revenue, VT sold, payouts owed, and every tip,
                        dance, and drink as it happens &mdash; so you always know exactly how the night is going.
                    </p>
                </div>

                <div className="mt-14">
                    <DashboardMockup/>
                </div>

                <div className="mx-auto mt-10 grid max-w-4xl grid-cols-1 gap-5 sm:grid-cols-3">
                    <div className="text-center">
                        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">Live revenue</p>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Track sales and your venue share in real time.</p>
                    </div>
                    <div className="text-center">
                        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">Clean payouts</p>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">See exactly what each dancer is owed before you settle up.</p>
                    </div>
                    <div className="text-center">
                        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">Full history</p>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Every tip, dance, and drink logged and searchable.</p>
                    </div>
                </div>
            </section>

            {/* Revenue / money story */}
            <section className="relative overflow-hidden bg-gray-900 py-20">
                <div className="pointer-events-none absolute -right-24 top-0 h-96 w-96 rounded-full bg-pink-600/20 blur-3xl"/>
                <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-3xl text-center">
                        <h2 className="text-base font-semibold uppercase tracking-wide text-pink-400">
                            The owner&apos;s bottom line
                        </h2>
                        <p className="mt-3 text-4xl font-bold tracking-tight text-white">
                            How your club makes more with VirtualTipz
                        </p>
                        <p className="mt-5 text-lg leading-relaxed text-gray-300">
                            The customer covers every cost of using VirtualTipz. Your club pays nothing to run it, and
                            earns two ways &mdash; a configurable share upfront on every package sold, plus an optional
                            automatic cut of tips and dances paid through the app.
                        </p>
                    </div>

                    <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="rounded-2xl bg-white/5 p-7 ring-1 ring-white/10">
                            <p className="text-3xl font-bold text-pink-400">Customer covers it</p>
                            <p className="mt-3 text-base leading-relaxed text-gray-300">
                                There&apos;s no fee to your business. The guest pays for their VirtualTipz package, so
                                every dollar the program earns you is upside &mdash; never a cost.
                            </p>
                        </div>
                        <div className="rounded-2xl bg-white/5 p-7 ring-1 ring-white/10">
                            <p className="text-3xl font-bold text-pink-400">Good only at your venue</p>
                            <p className="mt-3 text-base leading-relaxed text-gray-300">
                                VirtualTipz credits can only be spent at your club. A guest with a balance has a
                                reason to come back &mdash; it turns one-time visitors into repeat customers.
                            </p>
                        </div>
                    </div>

                    {/* Two revenue streams: upfront on sale + automatic cut on activity */}
                    <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="rounded-2xl bg-pink-600/10 p-7 ring-1 ring-pink-500/30">
                            <span className="inline-flex items-center rounded-full bg-pink-600/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-pink-300">
                                Revenue stream 1
                            </span>
                            <p className="mt-4 text-2xl font-bold text-white">Upfront on every package</p>
                            <p className="mt-3 text-base leading-relaxed text-gray-300">
                                Your configurable share of each package is money in your pocket the moment a guest buys
                                &mdash; collected right at the front desk, upfront, before a single tip is sent. You get
                                paid when they load, not later.
                            </p>
                        </div>
                        <div className="rounded-2xl bg-pink-600/10 p-7 ring-1 ring-pink-500/30">
                            <span className="inline-flex items-center rounded-full bg-pink-600/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-pink-300">
                                Revenue stream 2
                            </span>
                            <p className="mt-4 text-2xl font-bold text-white">A cut of tips &amp; dances</p>
                            <p className="mt-3 text-base leading-relaxed text-gray-300">
                                Optionally configure the app to automatically take a set percentage of every tip and
                                private dance paid through VirtualTipz &mdash; routed to your venue automatically, with
                                no counting and nothing to collect by hand.
                            </p>
                        </div>
                    </div>

                    {/* Configurable split breakdown on a $100 package (example: 4% / 1%) */}
                    <div className="mt-16 overflow-hidden rounded-3xl bg-white/5 ring-1 ring-white/10">
                        <div className="grid grid-cols-1 lg:grid-cols-2">
                            <div className="p-8 sm:p-10">
                                <p className="text-sm font-semibold uppercase tracking-wide text-pink-400">
                                    Example: the math on a $100 package
                                </p>
                                <p className="mt-3 text-2xl font-bold text-white">
                                    A guest buys $100 in VirtualTipz
                                </p>
                                <p className="mt-4 text-base leading-relaxed text-gray-300">
                                    The split is fully configurable. In this example your venue keeps 4% and the app
                                    takes 1% &mdash; so on a $100 package the guest gets 95 VirtualTipz to spend, and
                                    your club earns $4 upfront, the moment they buy. The guest covers the full cost,
                                    and the dancers still receive the full value tipped to them.
                                </p>
                            </div>
                            <div className="flex flex-col justify-center gap-4 border-t border-white/10 bg-black/20 p-8 sm:p-10 lg:border-l lg:border-t-0">
                                <div className="flex items-center justify-between">
                                    <span className="text-base text-gray-300">Guest pays</span>
                                    <span className="text-2xl font-bold text-white">$100</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-base text-gray-300">Guest gets to spend</span>
                                    <span className="text-2xl font-bold text-white">95 VT</span>
                                </div>
                                <div className="h-px bg-white/10"/>
                                <div className="flex items-center justify-between">
                                    <span className="text-base text-gray-300">Your venue (4%)</span>
                                    <span className="text-2xl font-bold text-pink-400">$4.00</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-base text-gray-300">VirtualTipz app (1%)</span>
                                    <span className="text-2xl font-bold text-gray-400">$1.00</span>
                                </div>
                                <div className="h-px bg-white/10"/>
                                <div className="flex items-center justify-between">
                                    <span className="text-base text-gray-300">Cost to your club</span>
                                    <span className="text-2xl font-bold text-white">$0</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-12 flex justify-center">
                        <a
                            href="#contact"
                            className="rounded-full bg-pink-600 px-8 py-4 text-base font-semibold text-white shadow-lg transition hover:bg-pink-500"
                        >
                            Talk to us about your floor
                        </a>
                    </div>
                </div>
            </section>

            {/* Rewards & custom packages */}
            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
                <div className="mx-auto max-w-3xl text-center">
                    <h2 className="text-base font-semibold uppercase tracking-wide text-pink-600">
                        Keep them coming back
                    </h2>
                    <p className="mt-3 text-4xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                        Build your own packages and reward loyalty
                    </p>
                    <p className="mt-5 text-lg leading-relaxed text-gray-600 dark:text-gray-400">
                        You control how VirtualTipz is priced and promoted at your club. Design the packages your
                        guests buy and run a rewards program that gives them a reason to spend more and return often.
                    </p>
                </div>

                <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-pink-100 text-2xl">
                            🎁
                        </div>
                        <h3 className="mt-5 text-xl font-semibold text-gray-900">Build custom VT packages</h3>
                        <p className="mt-3 text-base leading-relaxed text-gray-600">
                            Create your own VirtualTipz packages at the price points that fit your floor &mdash; from a
                            quick starter to a big-spender bundle. Add bonus VT to larger packages to push guests
                            toward loading more up front.
                        </p>
                    </div>
                    <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-pink-100 text-2xl">
                            ⭐
                        </div>
                        <h3 className="mt-5 text-xl font-semibold text-gray-900">Run a rewards program</h3>
                        <p className="mt-3 text-base leading-relaxed text-gray-600">
                            Reward your regulars for spending at your venue. Because VirtualTipz only works at your
                            club, every reward you give is another reason for a guest to walk back through your door.
                        </p>
                    </div>
                </div>
            </section>

            {/* Closing CTA */}
            <section className="bg-gradient-to-r from-pink-600 to-pink-500 py-20">
                <div className="mx-auto max-w-4xl px-6 text-center lg:px-8">
                    <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
                        Add a revenue stream that costs you nothing
                    </h2>
                    <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-pink-50">
                        Give your guests a faster way to tip, build the packages they buy, and earn on every one.
                        Let&apos;s get VirtualTipz live in your club.
                    </p>
                    <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
                        <a
                            href="#contact"
                            className="rounded-full bg-white px-8 py-4 text-base font-semibold text-pink-600 shadow-lg transition hover:bg-gray-100"
                        >
                            Get started
                        </a>
                        <a
                            href="#demo"
                            className="rounded-full px-8 py-4 text-base font-semibold text-white ring-1 ring-inset ring-white/60 transition hover:bg-white/10"
                        >
                            Replay the demo
                        </a>
                    </div>
                </div>
            </section>

            {/* Contact Us */}
            <section id="contact" className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
                <div className="overflow-hidden rounded-3xl bg-gray-900 shadow-xl">
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                        {/* Left: intro + entertainer note + contact links */}
                        <div className="p-10 sm:p-12">
                            <h2 className="text-base font-semibold uppercase tracking-wide text-pink-400">
                                Schedule a demo
                            </h2>
                            <p className="mt-3 text-4xl font-bold tracking-tight text-white">
                                Let&apos;s get VirtualTipz on your floor
                            </p>
                            <p className="mt-5 text-lg leading-relaxed text-gray-300">
                                Fill out the form and we&apos;ll be in touch soon.
                            </p>

                            <div className="mt-8 space-y-3">
                                <a
                                    href="tel:+19197103867"
                                    className="flex items-center gap-3 text-gray-300 transition hover:text-white"
                                >
                                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-pink-600 text-base">📞</span>
                                    <span className="text-sm font-semibold">(919) 710-3867</span>
                                </a>
                                <a
                                    href="mailto:pushenterprisetechnologies@gmail.com"
                                    className="flex items-center gap-3 text-gray-300 transition hover:text-white"
                                >
                                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-pink-600 text-base">✉️</span>
                                    <span className="truncate text-sm font-semibold">pushenterprisetechnologies@gmail.com</span>
                                </a>
                            </div>

                            <div className="mt-8">
                                <p className="text-lg font-semibold text-white">Aaron King</p>
                                <p className="text-base text-pink-400">VirtualTipz</p>
                            </div>
                        </div>

                        {/* Right: the form */}
                        <div className="border-t border-white/10 bg-black/20 p-10 sm:p-12 lg:border-l lg:border-t-0">
                            {demoSubmitted ? (
                                <div className="flex h-full flex-col items-center justify-center text-center">
                                    <span className="text-4xl">✅</span>
                                    <p className="mt-4 text-2xl font-bold text-white">Thanks — we got it!</p>
                                    <p className="mt-2 text-base text-gray-300">
                                        We received your request and will be in touch soon.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-medium uppercase tracking-wide text-gray-400">
                                            Name <span className="text-gray-500">(for clubs only)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={demo.name}
                                            onChange={(e) => updateDemo("name", e.target.value)}
                                            placeholder="Club owner / manager name"
                                            className="mt-1.5 block w-full rounded-md border-0 bg-white/5 px-3 py-2 text-white ring-1 ring-inset ring-white/15 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-pink-500 sm:text-sm"
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <label className="block text-xs font-medium uppercase tracking-wide text-gray-400">
                                                Email <span className="text-pink-400">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                value={demo.email}
                                                onChange={(e) => updateDemo("email", e.target.value)}
                                                placeholder="your@email.com"
                                                className="mt-1.5 block w-full rounded-md border-0 bg-white/5 px-3 py-2 text-white ring-1 ring-inset ring-white/15 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-pink-500 sm:text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium uppercase tracking-wide text-gray-400">
                                                Phone <span className="text-pink-400">*</span>
                                            </label>
                                            <input
                                                type="tel"
                                                value={demo.phone}
                                                onChange={(e) => updateDemo("phone", e.target.value)}
                                                placeholder="Phone number"
                                                className="mt-1.5 block w-full rounded-md border-0 bg-white/5 px-3 py-2 text-white ring-1 ring-inset ring-white/15 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-pink-500 sm:text-sm"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <label className="block text-xs font-medium uppercase tracking-wide text-gray-400">
                                                City
                                            </label>
                                            <input
                                                type="text"
                                                value={demo.city}
                                                onChange={(e) => updateDemo("city", e.target.value)}
                                                placeholder="City"
                                                className="mt-1.5 block w-full rounded-md border-0 bg-white/5 px-3 py-2 text-white ring-1 ring-inset ring-white/15 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-pink-500 sm:text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium uppercase tracking-wide text-gray-400">
                                                Affiliated club(s)
                                            </label>
                                            <input
                                                type="text"
                                                value={demo.affiliatedClubs}
                                                onChange={(e) => updateDemo("affiliatedClubs", e.target.value)}
                                                placeholder="Affiliated club(s)"
                                                className="mt-1.5 block w-full rounded-md border-0 bg-white/5 px-3 py-2 text-white ring-1 ring-inset ring-white/15 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-pink-500 sm:text-sm"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium uppercase tracking-wide text-gray-400">
                                            Message
                                        </label>
                                        <textarea
                                            rows={3}
                                            value={demo.message}
                                            onChange={(e) => updateDemo("message", e.target.value)}
                                            placeholder="Type your message..."
                                            className="mt-1.5 block w-full rounded-md border-0 bg-white/5 px-3 py-2 text-white ring-1 ring-inset ring-white/15 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-pink-500 sm:text-sm"
                                        />
                                    </div>

                                    {demoError && <p className="text-sm text-red-400">{demoError}</p>}

                                    <button
                                        type="button"
                                        onClick={submitDemo}
                                        disabled={demoSubmitting}
                                        className="w-full rounded-md bg-pink-600 px-5 py-3 text-base font-semibold text-white transition hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {demoSubmitting ? "Sending..." : "Schedule a demo"}
                                    </button>

                                    <p className="text-center text-xs text-gray-500">
                                        By submitting, you agree to our{" "}
                                        <span
                                            className="cursor-pointer underline hover:text-gray-300"
                                            onClick={() => navigate("/privacy")}
                                        >
                                            privacy policy
                                        </span>.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 py-10">
                <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 sm:flex-row lg:px-8">
                    <div className="text-xl font-extrabold tracking-tight">
                        <span className="text-white">VIRTUAL</span>
                        <span className="text-pink-500">TIPZ</span>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-gray-400">
                        <span className="cursor-pointer hover:text-white" onClick={() => navigate("/terms")}>Terms</span>
                        <span className="cursor-pointer hover:text-white" onClick={() => navigate("/privacy")}>Privacy</span>
                    </div>
                    <p className="text-sm text-gray-500">
                        &copy; {new Date().getFullYear()} Push Enterprise Technologies LLC.
                    </p>
                </div>
            </footer>
        </div>
    );
}
